import { supabase } from "@/integrations/supabase/client";
import { getCurrentClinicCustomerId, getCurrentClinicId } from "@/lib/utils/clinic-context";
import {
  createRelationshipRow,
  createStoredItemRow,
} from "@/pages/daycare-hotel/reservation/usePetProfileMutations";
import type { PetDaycareHotelOperation } from "./petDaycareHotelDraft";

type SaveResult = { failed: number };

function needsCustomer(operation: PetDaycareHotelOperation) {
  return operation.kind === "create-alert" || operation.kind === "create-relationship";
}

function needsClinic(operation: PetDaycareHotelOperation) {
  return operation.kind === "create-stored-item"
    || operation.kind === "create-recurring-activity"
    || operation.kind === "update-recurring-activity"
    || operation.kind === "deactivate-recurring-activity";
}

function localIsoDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

async function runOperation(
  petId: string,
  operation: PetDaycareHotelOperation,
  ids: { clinicId: string | null; customerId: string | null },
) {
  switch (operation.kind) {
    case "add-pack-membership": {
      const { error } = await supabase.from("stay_pack_members").upsert(
        { stay_pack_id: operation.packId, pet_id: petId },
        { onConflict: "stay_pack_id,pet_id" },
      );
      if (error) throw error;
      return;
    }
    case "remove-pack-membership": {
      const { error } = await supabase
        .from("stay_pack_members")
        .delete()
        .eq("stay_pack_id", operation.packId)
        .eq("pet_id", petId);
      if (error) throw error;
      return;
    }
    case "create-alert": {
      const { error } = await supabase.from("pet_custom_alerts").insert({
        pet_id: petId,
        customer_id: ids.customerId!,
        label: operation.label,
        value: operation.value,
        icon: null,
        color: operation.color,
      });
      if (error) throw error;
      return;
    }
    case "update-alert": {
      const { error } = await supabase
        .from("pet_custom_alerts")
        .update({ label: operation.label, value: operation.value, color: operation.color })
        .eq("id", operation.id);
      if (error) throw error;
      return;
    }
    case "deactivate-alert": {
      const { error } = await supabase
        .from("pet_custom_alerts")
        .update({ is_active: false })
        .eq("id", operation.id);
      if (error) throw error;
      return;
    }
    case "create-stored-item": {
      const { error } = await supabase.from("pet_stored_items").insert(
        createStoredItemRow({
          petId,
          clinicId: ids.clinicId!,
          itemName: operation.itemName,
          storageLocation: operation.storageLocation,
        }),
      );
      if (error) throw error;
      return;
    }
    case "update-stored-item": {
      const { error } = await supabase
        .from("pet_stored_items")
        .update({ item_name: operation.itemName, storage_location: operation.storageLocation })
        .eq("id", operation.id);
      if (error) throw error;
      return;
    }
    case "unkeep-stored-item": {
      const { error } = await supabase
        .from("pet_stored_items")
        .update({ is_kept: false })
        .eq("id", operation.id);
      if (error) throw error;
      return;
    }
    case "create-relationship": {
      const { error } = await supabase.from("pet_relationships").upsert(
        createRelationshipRow({
          customerId: ids.customerId!,
          petId,
          relatedPetId: operation.relatedPetId,
          relationship: operation.relationship,
        }),
        { onConflict: "pet_id,related_pet_id" },
      );
      if (error) throw error;
      return;
    }
    case "delete-relationship": {
      const { error } = await supabase.from("pet_relationships").delete().eq("id", operation.id);
      if (error) throw error;
      return;
    }
    case "create-recurring-activity": {
      const { data, error } = await supabase
        .from("stay_activities")
        .insert({
          clinic_id: ids.clinicId!,
          stay_activity_type_id: operation.typeId,
          target_type: "pet",
          target_pet_id: petId,
          target_stay_pack_id: null,
          scheduled_time: operation.scheduledTime,
          scheduled_date: localIsoDate(),
          description: operation.description,
          assigned_to: null,
          is_active: true,
        } as never)
        .select("id")
        .single();
      if (error) throw error;

      try {
        const { error: recurrenceError } = await supabase
          .from("stay_activity_recurrence")
          .insert({
            stay_activity_id: (data as { id: string }).id,
            frequency: "daily",
            interval_days: null,
            days_of_week: null,
            days_of_month: null,
            ends_on: null,
          });
        if (recurrenceError) throw recurrenceError;
      } catch (recurrenceError) {
        await supabase
          .from("stay_activities")
          .update({ is_active: false } as never)
          .eq("id", (data as { id: string }).id)
          .eq("clinic_id", ids.clinicId!);
        throw recurrenceError;
      }
      return;
    }
    case "update-recurring-activity": {
      const { error } = await supabase
        .from("stay_activities")
        .update({
          stay_activity_type_id: operation.typeId,
          description: operation.description,
          scheduled_time: operation.scheduledTime,
        } as never)
        .eq("id", operation.id)
        .eq("clinic_id", ids.clinicId!);
      if (error) throw error;
      return;
    }
    case "deactivate-recurring-activity": {
      const { error } = await supabase
        .from("stay_activities")
        .update({ is_active: false } as never)
        .eq("id", operation.id)
        .eq("clinic_id", ids.clinicId!);
      if (error) throw error;
      return;
    }
  }
}

/**
 * Grava as tabelas filhas da seção de creche e hotel. Nunca lança: o pet já foi
 * salvo neste ponto e não deve ser revertido por causa de uma tabela filha.
 * Devolve quantas operações falharam para o formulário avisar o usuário.
 */
export async function savePetDaycareHotelDraft(
  petId: string,
  operations: PetDaycareHotelOperation[],
): Promise<SaveResult> {
  if (operations.length === 0) return { failed: 0 };

  const clinicId = operations.some(needsClinic)
    ? await getCurrentClinicId()
    : null;
  const customerId = operations.some(needsCustomer) ? await getCurrentClinicCustomerId() : null;

  let failed = 0;
  for (const operation of operations) {
    try {
      await runOperation(petId, operation, { clinicId, customerId });
    } catch (error) {
      console.error("savePetDaycareHotelDraft", operation.kind, error);
      failed += 1;
    }
  }

  return { failed };
}
